import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { BrowserRouter, Route, Routes } from 'react-router';
import { useMemo, type ReactNode } from 'react';
import {
  Auth0ProviderWithRouter,
  RequireAuth,
  RequireVerifiedEmail,
  useAuthTokenWiring,
} from '@my/platform/auth';
import { directoryApi } from '@my/directory-api';
import { ToastContainer, GoogleMapsProvider } from '@my/ui';
import { Landmark } from 'lucide-react';
import { LandingWhenSignedOut } from '@/auth/LandingWhenSignedOut';
import { RequireSuperUser } from '@/auth/RequireSuperUser';
import { getConfig } from '@/configuration/config';
import { AppShell } from '@/layout/AppShell';
import { AccountSectionLayout } from '@/features/accounts/AccountSectionLayout';
import { AccountOrganisationsPage } from '@/features/accounts/AccountOrganisationsPage';
import { AccountOverviewPage } from '@/features/accounts/AccountOverviewPage';
import { AccountSubscriptionsPage } from '@/features/accounts/AccountSubscriptionsPage';
import { AccountUsersPage } from '@/features/accounts/AccountUsersPage';
import { AccountsPage } from '@/features/accounts/AccountsPage';
import { CreateAccountPage } from '@/features/accounts/CreateAccountPage';
import { NotFound } from '@/pages/NotFound';
import { PrivacyPage } from '@/pages/public/PrivacyPage';
import { TermsPage } from '@/pages/public/TermsPage';
import { useMapsConfig } from '@my/directory-api/hooks';

/**
 * Bridges Auth0 → axios. Has to live INSIDE Auth0Provider so the hook can read
 * its context. Renders nothing.
 */
function AuthTokenBridge() {
  useAuthTokenWiring();
  return null;
}

export function App() {
  const cfg = getConfig();

  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: 1,
            refetchOnWindowFocus: false,
            staleTime: 30_000,
          },
        },
      }),
    [],
  );

  return (
    <QueryClientProvider client={queryClient}>
      {/* The organisation panel's address editor searches Google Places, and
          `useMapsLibrary` needs this provider above it. Wrapped conditionally
          so a missing key degrades the address search rather than failing the
          whole app at boot. */}
      <BrowserRouter>
      <Auth0ProviderWithRouter
        settings={{
          domain: cfg.a0Domain,
          clientId: cfg.a0ClientId,
          audience: cfg.a0Audience,
          // NO `subscription`. My Hub is the account portal every account
          // has by definition — naming a licence the Action would check
          // would deny every login, since nobody subscribes to it.
        }}
      >
        <AuthTokenBridge />
        <Routes>
          {/* PUBLIC — no sign-in. The privacy policy and terms registered with
              Microsoft and Google must open for reviewers who have no account. */}
          <Route path="privacy" element={<PrivacyPage />} />
          <Route path="terms" element={<TermsPage />} />

          <Route
            element={
              <LandingWhenSignedOut>
                <RequireAuth brand={{ title: 'My Stratum', icon: Landmark }}>
                  <RequireVerifiedEmail
                    brand={{ title: 'My Stratum', icon: Landmark }}
                    onResend={() => directoryApi.resendMyVerificationEmail()}
                  >
                    {/* The temporary gate, and the ONLY place that asks about
                        super users. Everything below is written as the
                        customer's own portal — see RequireSuperUser. */}
                    <RequireSuperUser>
                      <DirectoryMapsProvider>
                        <AppShell />
                      </DirectoryMapsProvider>
                    </RequireSuperUser>
                  </RequireVerifiedEmail>
                </RequireAuth>
              </LandingWhenSignedOut>
            }
          >
            {/* The landing screen: pick a customer. Once one is chosen the
                sections take over, and the strip at the bottom switches
                between them — so there is no route back here to sign-post. */}
            <Route index element={<AccountsPage />} />
            <Route path="accounts/new" element={<CreateAccountPage />} />

            {/*
              NO ACCOUNT ID IN THESE PATHS, deliberately. An owner managing
              their own account has no id to put in a URL — theirs is simply
              /subscriptions — and building the staff version around a path
              parameter would give one screen two route shapes.
            */}
            <Route element={<AccountSectionLayout />}>
              <Route path="account" element={<AccountOverviewPage />} />
              <Route path="subscriptions" element={<AccountSubscriptionsPage />} />
              <Route path="organisations" element={<AccountOrganisationsPage />} />
              <Route path="users" element={<AccountUsersPage />} />
            </Route>
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Auth0ProviderWithRouter>
    </BrowserRouter>
      <ToastContainer />
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}

/**
 * Google Maps for the signed-in app, with the browser key from Directory
 * (`GET maps/config`) rather than the public config.json. Inside the
 * authenticated shell because the request needs the user's token.
 */
function DirectoryMapsProvider({ children }: { children: ReactNode }) {
  const maps = useMapsConfig();
  return (
    <GoogleMapsProvider apiKey={maps.data?.googleMapsApiKey} pending={maps.isPending}>
      {children}
    </GoogleMapsProvider>
  );
}
